/**
 * The shop's About copy — ONE module read by the browser bundle (/about) and the Worker (the
 * server-rendered /about page, llms.txt).
 *
 * ⚠️ No DOM, no React, no Worker types: imported by the Vite bundle as well.
 *
 * 🔴 Every sentence here is either the shop's existing copy (SHOP_SUMMARY / SHOP_ASSURANCES,
 * the footer line "since 2023") or an answer already on the FAQ page, taken from `buildFaq` so a
 * changed setting changes this too. Nothing is invented: no legal entity, owner, registration
 * number or founding story is stated, because none is in settings.
 */
import { hoursRows, type ResolvedContact } from "./shop-contact";
import { buildFaq, faqPlain, type FaqGroup } from "./shop-faq";

export const SHOP_SUMMARY =
  "Z S MERCH — tools, home & garden, electronics and everyday essentials at honest prices, delivered nationwide in 2–7 working days.";
export const SHOP_ASSURANCES = [
  "Delivery 2–7 working days",
  "Secure card & instant EFT checkout",
  "14-day returns on unused goods",
];

/** The footer's own line (Footer.tsx). */
export const SHOP_WHO = "Z S MERCH sells tools, home & garden, electronics and everyday essentials to retail and wholesale customers, and has done since 2023.";

/** One FAQ answer as plain text, or "" when the question is not in the FAQ. */
export function faqAnswer(groups: FaqGroup[], id: string): string {
  for (const g of groups) for (const i of g.items) if (i.id === id) return faqPlain(i.a).replace(/\n+/g, " ");
  return "";
}

export interface AboutSection {
  title: string;
  paragraphs: string[];
}

/** The About page's sections for the resolved contact facts. Empty paragraphs are dropped. */
export function aboutSections(r: ResolvedContact): AboutSection[] {
  const g = buildFaq(r);
  const sections: AboutSection[] = [
    { title: "Who we are", paragraphs: [SHOP_WHO, SHOP_SUMMARY] },
    {
      title: "Visit or collect",
      paragraphs: [
        `${r.collect.name}, ${r.collect.address}.`,
        `Trading hours: ${hoursRows(r.hours).map((h) => `${h.days} ${h.times}`).join("; ")}.`,
        faqAnswer(g, "collect-when"),
      ],
    },
    { title: "Delivery", paragraphs: [faqAnswer(g, "delivery-time"), faqAnswer(g, "delivery-fee")] },
    { title: "Payment", paragraphs: [faqAnswer(g, "payment")] },
    { title: "Returns", paragraphs: [faqAnswer(g, "returns-policy"), faqAnswer(g, "faulty")] },
    { title: "Wholesale accounts", paragraphs: [faqAnswer(g, "wholesale-account")] },
    {
      title: "Contact",
      paragraphs: [`WhatsApp ${r.contact.whatsapp}; phone ${r.contact.phone}; email ${r.contact.email}.`],
    },
  ];
  return sections.map((s) => ({ ...s, paragraphs: s.paragraphs.filter((p) => p.trim() !== "") })).filter((s) => s.paragraphs.length > 0);
}
